import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Heart,
  HandHeart,
  Shield,
  ExternalLink,
  Users,
  Sparkles,
} from "lucide-react";
import Nav from "@/components/sections/Nav";
import Footer from "@/components/sections/Footer";

const WARRIOR_LOGO = "/products/warrior-m-logo.png";

const PILLARS = [
  {
    icon: Heart,
    title: "Men's Health",
    body: "Funding mental wellness resources, peer-support groups, and screenings — because strength starts with self-care.",
  },
  {
    icon: Users,
    title: "Community Improvement",
    body: "Backing local cleanups, mentorship programs, and veteran-led service projects across Central Texas.",
  },
  {
    icon: Shield,
    title: "Brotherhood",
    body: "Creating a safe, inclusive, non-judgmental space for men to share, support each other, and thrive.",
  },
];

export default function Partners() {
  return (
    <div data-testid="partners-page" className="bg-background text-foreground min-h-screen">
      <Nav />

      <main className="pt-32 md:pt-40">
        {/* HERO — Unleash The Warrior Within */}
        <section
          id="warrior-hero"
          data-testid="warrior-hero"
          className="relative overflow-hidden grain"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0a0d0a] to-black pointer-events-none" />
          <div
            className="absolute inset-0 opacity-25 mix-blend-screen pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(ellipse at 30% 40%, rgba(127,176,105,0.35), transparent 60%), radial-gradient(ellipse at 70% 70%, rgba(127,176,105,0.18), transparent 65%)",
            }}
          />

          <div className="container-ic relative py-16 md:py-24">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary mb-10"
              data-testid="partners-back"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back home
            </Link>

            <div className="overline mb-4">
              <HandHeart className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />
              Ironclad × Warrior M Foundation
            </div>

            <h1
              className="font-display font-black uppercase leading-[0.9] tracking-tighter
                         text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] text-white"
              data-testid="warrior-headline"
            >
              <span className="block">Unleash the</span>
              <span className="block">
                Warrior <span className="text-primary">Within</span>
              </span>
            </h1>

            <p
              className="mt-8 text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed"
              data-testid="warrior-subcopy"
            >
              A percentage of every Ironclad service goes to support
              <span className="text-primary font-semibold"> Warrior M Foundation</span> —
              a non-profit dedicated to men's health and community improvement across
              Central Texas.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="https://www.warriormfoundation.org/donate"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="warrior-donate-btn"
              >
                <Heart className="h-4 w-4" /> Donate to Warrior M
              </a>
              <a
                href="#mission"
                className="btn-outline"
                data-testid="warrior-learn-btn"
              >
                Learn More
              </a>
            </div>
          </div>
        </section>

        {/* MISSION + LOGO */}
        <section
          id="mission"
          data-testid="partners-mission"
          className="py-20 md:py-28 bg-background border-t border-border"
        >
          <div className="container-ic">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              {/* Warrior M logo card */}
              <div className="lg:col-span-5">
                <div
                  className="border border-border bg-white p-12 flex items-center justify-center aspect-square"
                  data-testid="warrior-logo-card"
                >
                  <img
                    src={WARRIOR_LOGO}
                    alt="Warrior M Foundation logo"
                    className="w-full h-auto max-w-[320px] object-contain"
                  />
                </div>
                <p className="mt-4 text-xs uppercase tracking-widest text-muted-foreground text-center">
                  Warrior M Foundation · 501(c)(3) Non-Profit
                </p>
              </div>

              {/* Copy */}
              <div className="lg:col-span-7">
                <div className="overline mb-4">
                  <Sparkles className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />
                  Their Mission
                </div>
                <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter leading-[0.95]">
                  A safe place
                  <br />
                  <span className="text-primary">to thrive.</span>
                </h2>
                <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
                  Warrior M Foundation provides a safe, inclusive, non-judgmental space
                  for men to share their stories, receive support, and thrive. From
                  mental health resources to brotherhood-driven community projects, they
                  are reshaping what it means to ask for help — and to give it.
                </p>
                <p className="mt-4 text-muted-foreground text-lg leading-relaxed">
                  As a veteran-owned business, we believe strength is built shoulder to
                  shoulder. That's why Ironclad commits a portion of every job to
                  Warrior M's mission — and why we proudly stand beside them.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* PILLARS */}
        <section
          data-testid="partners-pillars"
          className="py-20 md:py-28 bg-background border-t border-border"
        >
          <div className="container-ic">
            <div className="max-w-3xl mb-14">
              <div className="overline mb-4">Where Your Dollars Go</div>
              <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter leading-[0.95]">
                Three pillars.
                <br />
                <span className="text-primary">One mission.</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
              {PILLARS.map((p, idx) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.title}
                    className="bg-background p-8 md:p-10"
                    data-testid={`pillar-${idx}`}
                  >
                    <div className="w-12 h-12 border border-primary/30 flex items-center justify-center mb-5">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-display text-2xl font-bold uppercase tracking-tight">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      {p.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section
          data-testid="partners-cta"
          className="py-20 md:py-28 bg-background border-t border-border"
        >
          <div className="container-ic">
            <div className="border border-border bg-card/30 p-10 md:p-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <div className="overline mb-3">Stand With Us</div>
                <h2 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tighter leading-[0.95]">
                  Hire Ironclad.
                  <br />
                  <span className="text-primary">Support a brother.</span>
                </h2>
                <p className="mt-5 text-muted-foreground text-base md:text-lg max-w-2xl">
                  Every booking funds Warrior M's work in our community. Want to do
                  more? Donate directly or share the mission with someone who needs to
                  hear it.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <a
                  href="/#booking"
                  className="btn-primary justify-center"
                  data-testid="partners-book-btn"
                >
                  Book an Ironclad Service
                </a>
                <a
                  href="https://www.warriormfoundation.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline justify-center"
                  data-testid="partners-warrior-link"
                >
                  Visit Warrior M
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
